import type { ScheduleForm } from './schedule-form'
import type { ReportTemplate } from '~/gql/reports/reports'
import { useMutation, useQuery } from '@apollo/client/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { GQL_DELETE_REPORT_SCHEDULE, GQL_REPORT_RECIPIENTS, GQL_UPSERT_REPORT_SCHEDULE } from '~/gql/reports/reports'
import { scheduleFormFrom, toScheduleInput } from './schedule-form'

/**
 * L'envoi planifié d'un modèle (#163). Les destinataires possibles viennent de
 * l'API, qui seule sait qui y a droit : l'encadrement actif, les formateurs
 * seulement s'ils peuvent exporter les statistiques.
 */
export function useReportSchedule(template: ReportTemplate, onSaved: () => void) {
  const { t } = useTranslation()
  const [form, setForm] = useState<ScheduleForm>(() => scheduleFormFrom(template.schedule))
  const [saving, setSaving] = useState(false)
  const { data } = useQuery(GQL_REPORT_RECIPIENTS)
  const [upsert] = useMutation(GQL_UPSERT_REPORT_SCHEDULE)
  const [remove] = useMutation(GQL_DELETE_REPORT_SCHEDULE)
  const recipients = data?.reportRecipients ?? []
  // Un destinataire qui n'y a plus droit (désactivé, rétrogradé…) n'est ni affiché
  // ni renvoyé : sinon l'enregistrement échouerait sur un compte invisible.
  const allowed = new Set(recipients.map(r => r.id))
  const recipientIds = form.recipientIds.filter(id => allowed.has(id))

  const run = async (action: () => Promise<unknown>, successKey: string) => {
    setSaving(true)
    try {
      await action()
      toast.success(t(successKey))
      onSaved()
    } catch {
      toast.error(t('reports.error'))
    } finally {
      setSaving(false)
    }
  }

  return {
    form: { ...form, recipientIds },
    setForm,
    recipients,
    saving,
    // Rien ne part avant que la liste soit là : on ne trierait pas encore les comptes qui n'y ont plus droit.
    canSave: data !== undefined && recipientIds.length > 0,
    save: () => run(() => upsert({ variables: { templateId: template.id, input: toScheduleInput({ ...form, recipientIds }) } }), 'reports.schedule.saved'),
    stop: () => run(() => remove({ variables: { templateId: template.id } }), 'reports.schedule.stopped'),
  }
}
